import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
import toEnglishNumber from '../utils/toEnglishNumber';
//
import { isValidToken, setSession } from './utils';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const response = await axios.get('/API/Services/GetUserData');

        const user = {
          about: '',
          address: '',
          city: 'تهران',
          country: 'ایران',
          displayName: '',
          email: '',
          id: '8864c717-587d-472a-929a-8e5f298024da-0',
          isPublic: true,
          password: '***',
          phoneNumber: '',
          photoURL:
            'https://api-dev-minimal-v4.vercel.app/assets/images/avatars/avatar_default.jpg',
          role: 'admin',
          roleId: '1',
          personelId : null,
          state: 'تهران',
          zipCode: '1111122222',
        };

        user.displayName = `${response.data[0].NameUser} ${response.data[0].Family}`;
        user.phoneNumber = response.data[0].UserN;
        user.email = response.data[0].Email;

        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (mobile, password) => {
    // console.log(
    //   'hostname => ',
    //   window.location.protocol,
    //   '//',
    //   window.location.hostname,
    //   '/Backend'
    // );

    const response = await axios.post('/API/UserLogin', {
      username: toEnglishNumber(mobile),
      password: toEnglishNumber(password),
    });
    const accessToken = response.data;

    setSession(accessToken);

    const roles = {
      1: 'مدیر سیستم',
      2: 'فروشنده',
      3: 'منو',
      4: 'کارگزار فروش',
      5: 'کارگزار خرید',
      6: 'عمومی',
      7: 'بازاریاب',
      8: 'پشتیبان',
    };

    const resUser = await axios.get('/API/Services/GetUserData');
    const user = {
      // about: 'واردات مواد اولیه نوشیدنی',
      // address: 'میرداماد ، خیابان ',
      // city: 'تهران',
      country: 'ایران',
      displayName: `${resUser.data[0].NameUser} ${resUser.data[0].Family}`,
      email: resUser.data[0].Email,
      id: resUser.Id,
      isPublic: true,
      // password: '123',
      phoneNumber: resUser.data[0].UserN,
      photoURL: '',
      role: roles[resUser.data[0].IdRoll],
      roleId: resUser.data[0].IdRoll,
      personelId : resUser.data[0].Perseneli,
      // state: 'تهران',
      // zipCode: '14377',
    };

    // user.displayName = `${resUser.data[0].NameUser} ${resUser.data[0].Family}`;
    // user.phoneNumber = resUser.data[0].UserN;
    // user.email = resUser.data[0].Email;
    // user.roleId = resUser.data[0].IdRoll;
    // user.role = roles[resUser.data[0].IdRoll];
    // user.photoURL = resUser.data[0].NameUser;

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(async (mobile, password, firstName, lastName, resendCode) => {
    // const response = await axios
    //   .post('/API/Services/ValidateRegUser', {
    //     mobileNO: mobile,
    //     nameU: firstName,
    //     family: lastName,
    //     otp: resendCode,
    //     ip: '127.0.0.1',
    //     pass: password,
    //   })
    //   .then(() => {
    //     if (response.status === 200) {
    //       console.log('register OK!');
    //     }
    //   });

    // const { accessToken, user } = response.data;

    // localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        isAuthenticated: false,
        user: null,
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null);

    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      login,
      register,
      logout,
    }),
    [state.isAuthenticated, state.isInitialized, state.user, login, logout, register]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
